import type { MouseEvent } from 'react';
import { generatePath } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import {
    VehicleCard,
    PersonCard,
    ConfidenceBadge,
    CollapsibleSection,
} from '@/components';
import { AppRoutes } from '@/constants/routes';
import { useOpenInNewWindow } from '@/hooks';
import { EngineService } from '@/services/engine';
import { useQueryResultsStore } from '@/stores';
import type { Person } from '@/types/person';
import { COMPACT_CARD_GRID_COLUMNS_CLASSES } from '@/constants/layout';
import { cn } from '@/lib/utils';

interface QueryPersonResultProps {
    person: Person;
    confidence: number;
    onClick: (personId: string, event?: MouseEvent) => void;
}

function QueryPersonResult({
    person,
    confidence,
    onClick,
}: QueryPersonResultProps) {
    const { data: currentPerson } = useSWR<Person>(
        `person-${person.id}`,
        () => EngineService.getPerson(person.id),
        { fallbackData: person, revalidateOnFocus: false },
    );

    return (
        <div className="relative">
            <div className="absolute top-2 left-2 z-10">
                <ConfidenceBadge value={confidence * 100} glass />
            </div>
            <PersonCard person={currentPerson ?? person} onClick={onClick} />
        </div>
    );
}

export function QueryIdentities() {
    const { t } = useTranslation(I18nNamespace.QUERIES);
    const vehicleResults = useQueryResultsStore(
        (state) => state.vehicleResults,
    );
    const personResults = useQueryResultsStore((state) => state.personResults);
    const { openUrl } = useOpenInNewWindow();

    const vehicleCount = vehicleResults?.length ?? 0;
    const personCount = personResults?.length ?? 0;

    if (vehicleCount === 0 && personCount === 0) {
        return null;
    }

    const handleVehicleClick = (vehicleId: string, event?: MouseEvent) => {
        openUrl(generatePath(AppRoutes.VEHICLE, { vehicleId }), event, {
            preserveSearchParams: false,
        });
    };

    const handlePersonClick = (personId: string, event?: MouseEvent) => {
        openUrl(generatePath(AppRoutes.PERSON, { personId }), event, {
            preserveSearchParams: false,
        });
    };

    return (
        <CollapsibleSection
            label={t('sections.identities')}
            count={vehicleCount + personCount}
        >
            <div
                className={cn('grid gap-4', COMPACT_CARD_GRID_COLUMNS_CLASSES)}
            >
                {personResults?.map(({ person, confidence }) => (
                    <QueryPersonResult
                        key={person.id}
                        person={person}
                        confidence={confidence}
                        onClick={handlePersonClick}
                    />
                ))}
                {vehicleResults?.map(({ vehicle, confidence }) => (
                    <div key={vehicle.id} className="relative">
                        <div className="absolute top-2 left-2 z-10">
                            <ConfidenceBadge value={confidence * 100} glass />
                        </div>
                        <VehicleCard
                            vehicle={vehicle}
                            thumbnailUrl={vehicle.thumbnailUrl}
                            onClick={handleVehicleClick}
                        />
                    </div>
                ))}
            </div>
        </CollapsibleSection>
    );
}
